import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MessageCircle, Send, X, Loader2 } from "lucide-react";
import { useLang } from "@/lib/i18n";

// Persist session_id per browser so the LLM keeps context across reloads.
function getSessionId() {
  let sid = localStorage.getItem("sukana_chat_session");
  if (!sid) {
    sid = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("sukana_chat_session", sid);
  }
  return sid;
}

export default function ChatWidget() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => [
    { id: "greet-1", from: "bot", text: t("chat.greeting") },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const sessionId = useRef(getSessionId()).current;

  // Update the greeting when language changes
  useEffect(() => {
    setMessages((m) => (m.length === 1 && m[0].from === "bot"
      ? [{ id: "greet-1", from: "bot", text: t("chat.greeting") }] : m));
  }, [lang, t]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    const userId = `u-${Date.now()}`;
    setMessages((m) => [...m, { id: userId, from: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/chatbot/message", { session_id: sessionId, message: text });
      setMessages((m) => [...m, { id: `b-${Date.now()}`, from: "bot", text: data.reply || "…" }]);
    } catch (err) {
      setMessages((m) => [...m, { id: `b-err-${Date.now()}`, from: "bot", text: t("chat.error") }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#1A3322] hover:bg-[#24472F] text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105"
          aria-label={t("chat.open")}
          data-testid="chatbot-open"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#C85A32] rounded-full animate-pulse"></span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[min(380px,calc(100vw-2rem))] h-[min(560px,calc(100vh-6rem))] bg-white rounded-2xl shadow-2xl border border-[#1A3322]/10 flex flex-col overflow-hidden"
          data-testid="chatbot-panel"
        >
          <div className="bg-[#1A3322] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center">
                <MessageCircle className="w-4 h-4"/>
              </div>
              <div>
                <p className="font-bold text-sm">{t("chat.title")}</p>
                <p className="text-[10px] text-white/70">{t("chat.subtitle")}</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="hover:bg-white/10 p-1 rounded" aria-label={t("chat.close")} data-testid="chatbot-close">
              <X className="w-4 h-4"/>
            </button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F5F5F0]/50" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <div key={m.id || `msg-${i}`} className={`flex ${m.from === "user" ? "justify-end" : "justify-start items-start gap-2"}`}>
                {m.from !== "user" && (
                  <img
                    src="https://customer-assets-4nw71qhi.emergentagent.net/job_sukana-launch/artifacts/9b5fhq23_LOGO%20SUKACOOP.PNG"
                    alt="SUKANACOOP"
                    className="w-7 h-7 rounded-full object-contain shrink-0 mt-1 border border-[#1A3322]/15 bg-white"
                  />
                )}
                <div className={`max-w-[80%] ${m.from === "user" ? "" : ""}`}>
                  {m.from !== "user" && (
                    <p className="text-[10px] font-bold text-[#1A3322] mb-0.5 pl-1">SUKANACOOP</p>
                  )}
                  <div className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-line ${
                    m.from === "user"
                      ? "bg-[#C85A32] text-white rounded-br-sm"
                      : "bg-white text-[#111812] rounded-bl-sm border border-[#1A3322]/10"
                  }`}>
                    {formatBotText(m.text)}
                  </div>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start items-start gap-2">
                <img
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_sukana-launch/artifacts/9b5fhq23_LOGO%20SUKACOOP.PNG"
                  alt="SUKANACOOP"
                  className="w-7 h-7 rounded-full object-contain shrink-0 mt-1 border border-[#1A3322]/15 bg-white"
                />
                <div className="bg-white border border-[#1A3322]/10 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-[#4B5563] flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin"/> {t("chat.thinking")}
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="p-3 border-t border-[#1A3322]/10 bg-white flex gap-2" data-testid="chatbot-form">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("chat.placeholder")}
              disabled={sending}
              className="flex-1 px-3 py-2 rounded-full border border-[#1A3322]/15 text-sm focus:outline-none focus:border-[#1A3322]"
              data-testid="chatbot-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="w-9 h-9 rounded-full bg-[#1A3322] hover:bg-[#24472F] disabled:opacity-40 text-white flex items-center justify-center"
              data-testid="chatbot-send"
            >
              <Send className="w-4 h-4"/>
            </button>
          </form>
        </div>
      )}
    </>
  );
}

// Very light markdown → JSX: bold **word** + line breaks
function formatBotText(raw) {
  if (!raw) return null;
  const lines = raw.split("\n");
  return lines.map((line, i) => (
    <span key={`line-${i}-${line.slice(0, 10)}`}>
      {line.split(/(\*\*[^*]+\*\*)/g).map((chunk, j) =>
        chunk.startsWith("**") && chunk.endsWith("**")
          ? <strong key={`b-${i}-${j}-${chunk.slice(0, 8)}`}>{chunk.slice(2, -2)}</strong>
          : <span key={`c-${i}-${j}-${chunk.slice(0, 8)}`}>{chunk}</span>
      )}
      {i < lines.length - 1 && <br/>}
    </span>
  ));
}
